import { MenuItem, Pagination, Select } from "@mui/material";
import "./CustomPagination.css";

/**
 * Shared pagination footer used across many list screens.
 *
 * ✅ Default behavior (used everywhere):
 * - Renders "Showing X–Y of Z" + page buttons.
 *
 * ✅ Optional "Rows per page" selector (currently opt-in):
 * - Pass BOTH `rowsPerPageOptions` and `onRowsPerPageChange` to show the selector.
 *
 * Example:
 * <CustomPagination
 *   totalRows={data.length}
 *   rowsPerPage={rowsPerPage}
 *   page={page}
 *   handlePage={setPage}
 *   rowsPerPageOptions={[25, 50, 75, 100]}
 *   onRowsPerPageChange={(next) => { setRowsPerPage(next); setPage(0); }}
 * />
 *
 * Note: This component does NOT slice your data or refetch server-side data.
 * - Client-side lists should continue to render `data.slice(page * rowsPerPage, ...)`.
 * - Server-side lists should update their `pageSize`/query params inside `onRowsPerPageChange`.
 */
const CustomPagination = ({
    tableData,
    totalRows,
    rowsPerPage,
    page,
    handlePage,
    rowsPerPageOptions,
    onRowsPerPageChange,
    rowsPerPageLabel = "Rows per page",
}) => {
    const rangeStart = totalRows ? page * rowsPerPage + 1 : 0;
    const rangeEnd = totalRows ? Math.min((page + 1) * rowsPerPage, totalRows) : 0;
    const pageCount = Math.max(1, Math.ceil(totalRows / rowsPerPage) || 1);

    const showRowsPerPage =
        Array.isArray(rowsPerPageOptions) &&
        rowsPerPageOptions.length > 0 &&
        typeof onRowsPerPageChange === "function";

    return (
        <div className="pagination-wrap">
            <div className="pagination-wrap__meta">
                {showRowsPerPage && (
                    <div className="pagination-rows-per-page">
                        <span className="pagination-rows-per-page__label">
                            {rowsPerPageLabel}
                        </span>
                        <Select
                            value={rowsPerPage}
                            onChange={(e) =>
                                onRowsPerPageChange(Number(e.target.value))
                            }
                            variant="standard"
                            disableUnderline
                            className="pagination-rows-per-page__select"
                            MenuProps={{
                                anchorOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                },
                            }}
                        >
                            {rowsPerPageOptions.map((n) => (
                                <MenuItem key={n} value={n} dense>
                                    {n}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                )}
                <p className="pagination-summary">
                    Showing {rangeStart}-{rangeEnd} of {totalRows}
                </p>
            </div>
            <Pagination
                page={page + 1}
                count={pageCount}
                color="primary"
                boundaryCount={1}
                onChange={(event, newPage) => handlePage(newPage - 1)}
            />
        </div>
    );
};

export default CustomPagination;
